import React, { createContext, useContext, useEffect } from 'react';
import { useNotifications } from '../hooks/useNotifications';
import { NotificationProvider } from './NotificationSnackbar';
import { setGlobalNotificationStore } from '../api/axios';

/**
 * Context per le notifiche globali
 */
const GlobalNotificationContext = createContext(null);

/**
 * Hook per usare le notifiche globali
 */
export const useGlobalNotifications = () => {
  const context = useContext(GlobalNotificationContext);
  if (!context) {
    throw new Error('useGlobalNotifications must be used within GlobalNotificationProvider');
  }
  return context;
};

/**
 * Provider per le notifiche globali
 * Questo componente deve wrappare l'intera applicazione per fornire
 * notifiche centralizate utilizzabili da qualsiasi componente
 */
export const GlobalNotificationProvider = ({ children, ...notificationProps }) => {
  const notifications = useNotifications();

  // Registra il sistema di notifiche con axios al montaggio
  useEffect(() => {
    setGlobalNotificationStore({
      showSuccess: notifications.showSuccess,
      showError: notifications.showError,
      showInfo: notifications.showInfo,
      showWarning: notifications.showWarning,
      handleApiError: notifications.handleApiError,
      handleApiSuccess: notifications.handleApiSuccess,
      setLoading: (loading) => {
        // Qui potresti gestire un loading globale se necessario
        // Per ora usiamo solo le notifiche
      },
      clear: notifications.clear,
      clearAll: notifications.clearAll
    });

    // Cleanup al dismount
    return () => {
      setGlobalNotificationStore(null);
    };
  }, [notifications]);

  return (
    <GlobalNotificationContext.Provider value={notifications}>
      <NotificationProvider
        notifications={notifications}
        onClear={notifications.clear}
        {...notificationProps}
      >
        {children}
      </NotificationProvider>
    </GlobalNotificationContext.Provider>
  );
};

/**
 * HOC per iniettare notifiche in qualsiasi componente
 */
export const withNotifications = (WrappedComponent) => {
  return (props) => {
    const notifications = useGlobalNotifications();
    return <WrappedComponent {...props} notifications={notifications} />;
  };
};

/**
 * Hook semplificato per operazioni API comuni
 */
export const useApiOperations = () => {
  const notifications = useGlobalNotifications();

  /**
   * Esegue un'operazione API con gestione automatica delle notifiche
   */
  const executeOperation = async (
    operation,
    {
      successMessage = null,
      errorMessage = null,
      showLoading = true,
      suppressNotifications = false
    } = {}
  ) => {
    try {
      if (showLoading && !suppressNotifications) {
        notifications.showInfo('Operazione in corso...');
      }

      const result = await operation();

      if (!suppressNotifications) {
        notifications.clear('info'); // Rimuovi il messaggio di loading
        if (successMessage) {
          notifications.showSuccess(successMessage);
        }
      }

      return result;
    } catch (error) {
      if (!suppressNotifications) {
        notifications.clear('info'); // Rimuovi il messaggio di loading
        notifications.handleApiError(error, errorMessage);
      }
      throw error;
    }
  };

  /**
   * Operazioni CRUD comuni
   */
  const createRecord = (apiCall, successMessage = 'Record creato con successo') => 
    executeOperation(apiCall, { successMessage });

  const updateRecord = (apiCall, successMessage = 'Record aggiornato con successo') => 
    executeOperation(apiCall, { successMessage });

  const deleteRecord = (apiCall, successMessage = 'Record eliminato con successo') => 
    executeOperation(apiCall, { successMessage });

  const fetchData = (apiCall, errorMessage = 'Errore nel caricamento dei dati') => 
    executeOperation(apiCall, { errorMessage, suppressNotifications: true });

  return {
    executeOperation,
    createRecord,
    updateRecord,
    deleteRecord,
    fetchData,
    notifications
  };
};

/**
 * Hook per gestire form con notifiche automatiche
 */
export const useFormNotifications = () => {
  const notifications = useGlobalNotifications();

  const handleSubmitWithNotification = async (
    submitFunction,
    {
      successMessage = 'Dati salvati con successo',
      errorMessage = 'Errore durante il salvataggio',
      resetFormOnSuccess = false,
      resetForm = null
    } = {}
  ) => {
    try {
      notifications.clearAll(); // Pulisci notifiche precedenti
      
      const result = await submitFunction();
      
      notifications.showSuccess(successMessage);
      
      if (resetFormOnSuccess && resetForm) {
        resetForm();
      }
      
      return result;
    } catch (error) {
      notifications.handleApiError(error, errorMessage);
      throw error;
    }
  };

  const handleValidationError = (errors) => {
    const errorMessages = Array.isArray(errors) 
      ? errors.join(', ')
      : typeof errors === 'string' 
        ? errors 
        : 'Errori di validazione del form';
    
    notifications.showError(errorMessages);
  };

  return {
    handleSubmitWithNotification,
    handleValidationError,
    notifications
  };
};

export default GlobalNotificationProvider;